import DateSelector from '@/components/features/shared/DateSelector'
import { NavHeader } from '@/components/features/shared/NavHeader'
import { SelectSearch } from '@/components/features/shared/SelectSearch'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { UserStatus } from '@/domain/auth/auth'
import { formatRedemptionType, RedemptionType } from '@/domain/redeem/redeem'
import { useAgreement, useLatestContractNumber } from '@/hooks/api/useAgreements'
import { useAssuredPeopleByContract } from '@/hooks/api/useAssuredPeople'
import { useClient, useClients } from '@/hooks/api/useClients'
import { useOfferings } from '@/hooks/api/useOfferings'
import { useParcel } from '@/hooks/api/useParcels'
import { useCompleteRedemption, useRedemptionsByContract } from '@/hooks/api/useRedemptions'
import { Paths } from '@/routes/paths'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate, useParams } from '@tanstack/react-router'
import { MapPin, User } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { PageContainer } from '@/components/features/shared/PageContainer'

const schema = z.object({
  userId: z.string().min(1, 'Debe seleccionar un cliente'),
  offeringId: z.string().min(1, 'Debe seleccionar un servicio'),
  contractNumber: z.string().min(1, 'Debe ingresar un número de contrato'),
  priceDifference: z.coerce.number().min(0, 'La diferencia no puede ser negativa'),
  endDate: z.date().optional(),
  deathDate: z.date().optional()
})

type FormData = z.infer<typeof schema>

export function CompleteRedemptionPage() {
  const { contractId, redemptionId } = useParams({ strict: false }) as { contractId: string; redemptionId: string }
  const navigate = useNavigate()

  const completeMutation = useCompleteRedemption()
  const { data: offerings } = useOfferings()
  const [clientSearch, setClientSearch] = useState('')
  const clientFilters = useMemo(
    () => ({ search: clientSearch, status: UserStatus.Active, limit: 100 }),
    [clientSearch],
  )

  const { data: clientsResponse } = useClients(clientFilters)

  const { data: agreement } = useAgreement(contractId)
  const { data: assuredPeople } = useAssuredPeopleByContract(contractId)
  const { data: redemptions, isLoading: isLoadingRedemption } = useRedemptionsByContract(contractId)
  const redemption = redemptions?.find(r => r.id === redemptionId)
  const { data: parcel } = useParcel(redemption?.parcelId!)
  const clients = clientsResponse?.data

  const form = useForm<FormData>({
    resolver: zodResolver(schema) as any,
    defaultValues: {
      userId: '',
      offeringId: '',
      contractNumber: '',
      priceDifference: 0,
      endDate: undefined,
      deathDate: undefined,
    }
  })

  const selectedUserId = form.watch('userId')
  const { data: selectedClient } = useClient(selectedUserId)

  const availableOfferings = useMemo(
    () =>
      offerings?.filter(
        (o) =>
          ((redemption?.type === RedemptionType.CREMATION ||
            redemption?.type === RedemptionType.FUNERAL_CREMATION) &&
            o.code === '21') ||
          ((redemption?.type === RedemptionType.PARCEL ||
            redemption?.type === RedemptionType.FUNERAL_PARCEL) &&
            o.code === '2') ||
          ((redemption?.type === RedemptionType.FUNERAL) && o.code === '70')
      ) || [],
    [offerings, redemption?.type]
  )

  const clientItems = useMemo(
    () => [
      ...(clients || [])
        .filter(
          (client) => client.id !== agreement?.userId && client.id !== selectedClient?.id
        )
        .map((client) => ({
          id: client.id,
          label: `${client.name} - N° ${client.code}`,
        })),
      ...(selectedClient && selectedClient.id !== agreement?.userId
        ? [
          {
            id: selectedClient.id,
            label: `${selectedClient.userData.legalName} - N° ${selectedClient.userData.code}`,
          },
        ]
        : []),
    ],
    [clients, agreement?.userId, selectedClient?.id]
  )

  const selectedOfferingId = form.watch('offeringId')
  const selectedOffering = availableOfferings.find(o => o.id === selectedOfferingId)
  const currentOffering = offerings?.find(offering => offering.id === agreement?.offeringId)
  const serviceTypeToRedeem = selectedOffering ? selectedOffering.serviceType : undefined
  const isCremation = selectedOffering?.code === '21' || selectedOffering?.code === '70'

  const { data: latestContractNumber } = useLatestContractNumber(serviceTypeToRedeem)

  useEffect(() => {
    if (latestContractNumber) {
      const match = latestContractNumber.match(/^(C-|P-)(\d+)$/);
      if (match) {
        const nextNumber = parseInt(match[2], 10) + 1;
        form.setValue('contractNumber', `${match[1]}${String(nextNumber).padStart(match[2].length, '0')}`);
      } else {
        form.setValue('contractNumber', latestContractNumber);
      }
    }
  }, [latestContractNumber])

  const onSubmit = async (data: any) => {
    if (!redemptionId || !contractId) return

    try {
      await completeMutation.mutateAsync({
        id: redemptionId,
        data: {
          userId: data.userId,
          contractNumber: data.contractNumber,
          offeringId: data.offeringId,
          priceDifference: data.priceDifference,
          endDate: data.endDate,
          deathDate: data.deathDate
        }
      })
      navigate({ to: `${Paths.Management.Contracts}/${contractId}` })
    } catch (e) {
      console.error(e)
    }
  }

  if (isLoadingRedemption) return <div>Cargando...</div>

  return (
    <PageContainer className='max-w-2xl'>
      <NavHeader>
        <h1 className="text-2xl font-bold tracking-tight">Completar Redención</h1>
        <p className="text-muted-foreground">
          {agreement ? `Contrato: ${agreement.contractNumber}` : 'Cargando...'}
        </p>
      </NavHeader>

      {agreement && (
        <Card>
          <CardHeader>
            <CardTitle>Cliente a Redimir</CardTitle>
            <p className="text-md">
              <span className='flex items-center gap-2'>
                <User className='w-4' />
                {agreement.userName}
              </span>
            </p>
          </CardHeader>
          <CardContent>
            <div className='flex flex-col gap-2 mb-4'>
              <span className='text-md font-semibold'>Personas a Redimir:</span>
              {assuredPeople && assuredPeople.filter(p => redemption?.ids.includes(p.id!)).map(assuredPerson => (
                <p className="text-sm">
                  <span className='flex items-center gap-2'>
                    <User className='w-4' />
                    {assuredPerson.name}
                  </span>
                </p>
              ))}
            </div>
            <p className="text-sm"><strong>Contrato origen:</strong> {agreement.contractNumber}</p>
            <p className="text-sm"><strong>Abono original:</strong> {currentOffering?.code} - {currentOffering?.name}</p>
            {redemption && (
              <p className="text-sm"><strong>Tipo de redención elegida:</strong> {formatRedemptionType(redemption.type)}</p>
            )}
            {parcel && (
              <div className="mt-3 pt-3 border-t">
                <p className="text-sm font-semibold"></p>
                <span className='flex items-center gap-2'>
                  <MapPin className='w-4' />
                  Parcela seleccionada
                </span>
                <p className="text-sm"><strong>Sección:</strong> {parcel.section}</p>
                <p className="text-sm"><strong>Fila:</strong> {parcel.fila}</p>
                <p className="text-sm"><strong>Parcela:</strong> {parcel.parcela}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Completar Redención</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="userId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cliente</FormLabel>
                    <SelectSearch
                      value={field.value}
                      onValueChange={(value) => field.onChange(value)}
                      onSearchChange={setClientSearch}
                      placeholder="Seleccionar cliente"
                      searchPlaceholder="Buscar cliente..."
                      emptyMessage="No se encontraron clientes."
                      items={clientItems}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="offeringId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Servicio a Prestar</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccione un servicio" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {availableOfferings.map(offering => (
                          <SelectItem key={offering.id} value={offering.id}>
                            {offering.name} ({offering.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="contractNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Número de Contrato</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="priceDifference"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Diferencia de Precio ($)</FormLabel>
                    <FormControl>
                      <Input type="number" {...field} value={field.value as any} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {isCremation && (
                <FormField
                  control={form.control}
                  name="deathDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fecha de Fallecimiento</FormLabel>
                      <FormControl>
                        <DateSelector initialDate={field.value} onDateSelected={(date: Date) => { field.onChange(date) }} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {isCremation && (
                <FormField
                  control={form.control}
                  name="endDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fecha de Cremación</FormLabel>
                      <FormControl>
                        <DateSelector initialDate={field.value} onDateSelected={(date: Date) => { field.onChange(date) }} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <div className="flex justify-between pt-4">
                <Button type="button" variant="outline" onClick={() => navigate({ to: `${Paths.Management.Contracts}/${contractId}` })}>
                  Cancelar
                </Button>
                <Button type="submit" variant={"info"} disabled={completeMutation.isPending}>
                  Confirmar Redención
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </PageContainer>
  )
}
